/**
 * Where a `{name}` resolves: SICP's environment model (§4.1.3). An
 * environment is a list of frames, innermost first, and the operations on it
 * are plain functions on `EnvironmentLogic` that take the environment as
 * their first argument, the way every `*Logic` namespace takes its receiver:
 * `extendEnvironment` puts a frame in front, `resolve` walks the frames
 * outward to the first that binds a name.
 *
 * One message composes in a list of frames, innermost first:
 *
 * 1. The variable frames at a slot's position: one frame per variable
 *    defined (`define`) before it, the latest innermost. A later slot
 *    defining the same name rebinds it from that point on. An answer-taking
 *    slot left blank defines nothing.
 * 2. The blank frame: every variable the form defines, bound to `''`, so a
 *    variable only another sharing mode's slots define reads as blank.
 * 3. The recipient frame: the primitives one message's recipients and
 *    leg decide, such as their names and the topic link.
 * 4. The intent frame: the primitives one intent's messages share, such
 *    as a reshare's story.
 * 5. The topic type's frames: the names the topic's type defines in its
 *    `definitions.yaml` (see `topic_garden/topic_type_def.ts`), such as a news topic's
 *    `_headline`, one frame per define as a form's are, each bound to a
 *    thunk of its expression.
 * 6. The share frame: the primitives fixed for an entire sharing session (the
 *    topic's name, its dates, the sender's name).
 * 7. The base frame: the primitive procedures, such as `truncate`
 *    (`baseEnvironment`, SICP's `setup-environment`).
 *
 * The share, intent and recipient frames are added with `extendWithDefinitions`, which
 * evaluates a list of primitive definitions over the facts it is given
 * (see `primitive.ts`); their values are plain strings. The form's
 * frames are built over the recipient frame by `ComposeLogic.evaluateForm`,
 * so a select option naming the recipient is evaluated where the recipient
 * is in reach; their values are thunks, evaluated the first time
 * they are looked up.
 */

import type { TopicTypeId } from '@/modules/topic/types';
import { TopicTypeDefLogic } from '@/modules/topic_garden/topic_type_def';
import {
  INTENT_DEFINITIONS,
  RECIPIENT_DEFINITIONS,
  SHARE_DEFINITIONS,
  PROCEDURE_DEFINITIONS,
  type ShareFacts,
  type PrimitiveDefinition,
  type ProcedureDefinition,
  type Value,
} from './primitive';
import { StatementLogic } from './statement';

/**
 * Names bound to their values, and the facts the frame's primitives were
 * defined over. Template text never sees the facts; only definitions read
 * them.
 */
export interface Frame {
  readonly bindings: ReadonlyMap<string, Binding>;
  readonly facts?: object;
  /**
   * What a person calls each name this frame binds, for the names defined
   * with one. A doc belongs to the binding, not the value: `_headline` and
   * `_title` hold the same value under different docs.
   */
  readonly docs?: ReadonlyMap<string, string>;
}

declare const factsType: unique symbol;

/**
 * A list of frames, innermost first. `F` is the facts its frames carry
 * between them (see `factsOf`); it exists only for the type checker.
 */
export type Environment<F = unknown> = readonly Frame[] & {
  readonly [factsType]?: F;
};

/** What a name is bound to: a value, a slot's thunk, or a procedure. */
export type Binding = string | Thunk | PrimitiveProcedure;

/** A primitive procedure as a binding: SICP's `(primitive <implementation>)`. */
export interface PrimitiveProcedure {
  readonly tag: 'primitive';
  readonly procedure: ProcedureDefinition;
}

/**
 * What an expression evaluates to: a value, or the procedure a name is bound
 * to.
 */
export type EvaluatedValue = Value | PrimitiveProcedure;

/**
 * How a define computes its value in an environment: its expression, already
 * analyzed (SICP §4.1.7). A select option `Hey {_recipientInformalName}`
 * evaluates to the greeting with this message's recipient in it; a fill-in
 * evaluates to the sender's words as they are, so braces they type stay
 * text; a topic type's `_shout` defined as `uppercase` evaluates to the
 * procedure.
 */
export type ExecutionProcedure = (env: Environment) => EvaluatedValue;

/**
 * A name bound to an expression rather than a value, memoized as SICP's thunk
 * (§4.2.2): delayed until first looked up, then forced in the environment it
 * was bound with, and from then on the value alone. A thunk looked up while
 * it is being forced (two fields naming each other, which the schema rejects)
 * reads as blank rather than looping.
 */
export interface Thunk {
  state:
    | {
        readonly tag: 'delayed';
        readonly expr: ExecutionProcedure;
        readonly env: Environment;
      }
    | { readonly tag: 'forcing' }
    | { readonly tag: 'forced'; readonly value: EvaluatedValue };
}

// `extendWithDefinitions` takes its facts' types from the environment and the
// facts alone, so a definition list cannot widen what the new frame is said
// to know.
type NoInfer<T> = [T][T extends unknown ? 0 : never];

const theEmptyEnvironment: Environment = [];

function makeFrame(
  bindings: ReadonlyMap<string, Binding>,
  facts?: object,
  docs?: ReadonlyMap<string, string>
): Frame {
  return { bindings, facts, docs };
}

function extendEnvironment<F>(
  enclosing: Environment<F>,
  frame: Frame
): Environment<F> {
  return [frame, ...enclosing] as Environment<F>;
}

function delay(expr: ExecutionProcedure, env: Environment): Thunk {
  return { state: { tag: 'delayed', expr, env } };
}

const FORCING = { tag: 'forcing' } as const;

function forceIt(binding: Thunk): EvaluatedValue {
  const { state } = binding;
  if (state.tag === 'forced') return state.value;
  if (state.tag === 'forcing') return '';
  binding.state = FORCING;
  try {
    const value = state.expr(state.env);
    binding.state = { tag: 'forced', value };
    return value;
  } catch (error) {
    binding.state = state;
    throw error;
  }
}

/** The innermost binding of `name` in `env`: SICP's frame scan. */
function lookup(env: Environment, name: string): Binding | undefined {
  for (const frame of env) {
    const binding = frame.bindings.get(name);
    if (binding !== undefined) return binding;
  }
  return undefined;
}

/**
 * The doc of the binding of `name` in scope in `env`: the innermost frame
 * binding `name` holds it. Undefined when that binding has none, or when
 * nothing binds `name`.
 */
function docOf(env: Environment, name: string): string | undefined {
  for (const frame of env) {
    if (frame.bindings.has(name)) return frame.docs?.get(name);
  }
  return undefined;
}

/** Every name `env` binds, each once, innermost first. */
function namesIn(env: Environment): readonly string[] {
  return Array.from(
    new Set(env.flatMap(frame => Array.from(frame.bindings.keys())))
  );
}

/** Each of `definitions`' names mapped to its doc. */
function docsOf(
  definitions: readonly { readonly name: string; readonly doc: string }[]
): ReadonlyMap<string, string> {
  return new Map(definitions.map(({ name, doc }) => [name, doc]));
}

/** Whether `value` is a primitive procedure: SICP's `primitive-procedure?`. */
function isPrimitiveProcedure(value: unknown): value is PrimitiveProcedure {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as Partial<PrimitiveProcedure>).tag === 'primitive'
  );
}

/**
 * What `name` is bound to in `env`: the value of the innermost binding — text,
 * `''` for a name declared but unassigned, a procedure, or whatever a
 * define's expression evaluated to — and undefined
 * for a name nothing binds. SICP's `lookup-variable-value`, forcing a thunk it
 * finds. A call's operator is looked up here like any other name.
 */
function lookupVariableValue(
  env: Environment,
  name: string
): EvaluatedValue | undefined {
  const binding = lookup(env, name);
  if (
    binding === undefined ||
    typeof binding === 'string' ||
    isPrimitiveProcedure(binding)
  ) {
    return binding;
  }
  return forceIt(binding);
}

/**
 * What `{name}` prints in `env`: `lookupVariableValue` for a name bound to
 * text. Only text prints, so naming a procedure or another value throws;
 * validation rejects such a token before it gets here.
 */
function resolve(env: Environment, name: string): string | null {
  const value = lookupVariableValue(env, name);
  if (isPrimitiveProcedure(value)) {
    throw new Error(`${name} is a procedure; call it as ${name}(…)`);
  }
  if (value !== undefined && typeof value !== 'string') {
    throw new Error(`${name} is a ${typeof value}, not text`);
  }
  return value ?? null;
}

/**
 * `env` with `name` defined as `value`, for what is evaluated in the
 * environment it returns: SICP's `define-variable!`, as a new frame binding
 * just that name, so what was evaluated in `env` keeps the binding it saw and
 * a later define of the same name rebinds it from that point on. `doc`, when
 * given, is recorded beside the binding (see `docOf`).
 */
function defineVariable<F>(
  env: Environment<F>,
  name: string,
  value: Binding,
  doc?: string
): Environment<F> {
  return extendEnvironment(
    env,
    makeFrame(
      new Map([[name, value]]),
      undefined,
      doc === undefined ? undefined : new Map([[name, doc]])
    )
  );
}

/** The facts `env`'s frames carry, an inner frame's overriding an outer's. */
function factsOf<F>(env: Environment<F>): F {
  return Object.assign(
    {},
    ...[...env].reverse().map(frame => frame.facts ?? {})
  ) as F;
}

/**
 * `enclosing` extended with a frame defined by `definitions`: each is
 * evaluated over `facts` and the facts `enclosing` already has, and binds its name in the
 * new frame.
 */
function extendWithDefinitions<F, C extends object>(
  enclosing: Environment<F>,
  facts: C,
  definitions: readonly PrimitiveDefinition<NoInfer<F & C>>[]
): Environment<F & C> {
  const bindings = new Map<string, Binding>();
  const env = extendEnvironment(
    enclosing,
    makeFrame(bindings, facts, docsOf(definitions))
  ) as Environment<F & C>;
  const known = factsOf(env);
  for (const definition of definitions) {
    bindings.set(definition.name, definition.bootstrapValue(known));
  }
  return env;
}

/**
 * The environment every other is built on: one frame binding each primitive
 * procedure.
 */
const baseEnvironment: Environment = extendEnvironment(
  theEmptyEnvironment,
  makeFrame(
    new Map<string, Binding>(
      PROCEDURE_DEFINITIONS.map(procedure => [
        procedure.name,
        { tag: 'primitive', procedure },
      ])
    ),
    undefined,
    docsOf(PROCEDURE_DEFINITIONS)
  )
);

/**
 * `share` with the names a topic of `type` defines in its `definitions.yaml`
 * (see `topic_garden/topic_type_def.ts`) pushed over it: its define statements run in order
 * by `StatementLogic.run`, as a form's are. Nothing pops them: the
 * environment is dropped when the session ends, and its definitions with it.
 * With no type, every type's definitions.
 */
function topicTypeEnvironment<F>(
  share: Environment<F>,
  type?: TopicTypeId
): Environment<F> {
  return StatementLogic.run(TopicTypeDefLogic.definitionsOf(type), share);
}

/**
 * The share environment: the primitives fixed for an entire sharing session,
 * and over them what the topic's type defines (`topicTypeEnvironment`).
 */
function shareEnvironment(facts: ShareFacts): Environment<ShareFacts> {
  return topicTypeEnvironment(
    extendWithDefinitions(baseEnvironment, facts, SHARE_DEFINITIONS),
    facts.topic.type ?? 'event'
  );
}

/**
 * `enclosing` with a frame binding each of `definitions`' names, under its
 * doc where it has one, to empty text: each name is bound to text, though not
 * to its value.
 */
function touchBindings<F>(
  enclosing: Environment<F>,
  definitions: readonly { readonly name: string; readonly doc?: string }[]
): Environment<F> {
  return extendEnvironment(
    enclosing,
    makeFrame(
      new Map(definitions.map(({ name }) => [name, ''])),
      undefined,
      new Map(
        definitions.flatMap(({ name, doc }) =>
          doc === undefined ? [] : [[name, doc] as const]
        )
      )
    )
  );
}

/**
 * The names a share environment binds, outside any session: the share
 * primitives, touched, over the procedures. This simulates the bindings we
 * would have if we were in a real sharing session.
 */
const shareEnclosingNamespace: Environment = touchBindings(
  baseEnvironment,
  SHARE_DEFINITIONS
);

const FORM_ENCLOSING_NAMESPACES = new Map<
  TopicTypeId | undefined,
  Environment
>();

/**
 * Every name bound around a form for a topic of `type`, outermost first: the
 * procedures, the share primitives (touched), the names `type` defines (with
 * no type, every type's) run over them as in a session, then the intent and
 * recipient primitives (touched). A name is a procedure, text, or unbound
 * here, simulating the bindings we would have in a message scope.
 */
function formEnclosingNamespace(type?: TopicTypeId): Environment {
  let env = FORM_ENCLOSING_NAMESPACES.get(type);
  if (!env) {
    env = touchBindings(
      touchBindings(
        topicTypeEnvironment(shareEnclosingNamespace, type),
        INTENT_DEFINITIONS
      ),
      RECIPIENT_DEFINITIONS
    );
    FORM_ENCLOSING_NAMESPACES.set(type, env);
  }
  return env;
}

export const EnvironmentLogic = {
  theEmptyEnvironment,
  baseEnvironment,
  makeFrame,
  extendEnvironment,
  extendWithDefinitions,
  defineVariable,
  delay,
  touchBindings,
  lookupVariableValue,
  docOf,
  namesIn,
  isPrimitiveProcedure,
  resolve,
  factsOf,
  topicTypeEnvironment,
  shareEnvironment,
  shareEnclosingNamespace,
  formEnclosingNamespace,
};
